import instance from "@/libs/axios";

/**
 * @name reportReasonService
 * @description Service for managing reported users in the admin panel
 */

/**
 * Get list of user reports with pagination, search, and filters
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.limit - Items per page (default: 10)
 * @param {string} params.search - Search reporter/reported name, username, reason
 * @param {string} params.user_status - Filter by reported user status (all, active, deactivated).
 *   Only meaningful on the "user" tab - the backend does not apply it to feed reports.
 * @param {string} params.start_date - Filter by start date (ISO format)
 * @param {string} params.end_date - Filter by end date (ISO format)
 * @param {string} params.content_type - Which Reports sub-tab to read: "user" (default, the
 *   original reported-users table) or "feed" (the combined "Reported Post and Stories" tab,
 *   covering both content types in one list - comments are not reportable).
 * @param {string} params.feed_content_type - Only applies when content_type="feed": narrows
 *   to "post" or "story"; omit for both. Ignored on the "user" tab.
 * @returns {Promise} API response with user reports list and pagination
 */
const getReportReasons = async (params = {}) => {
  const {
    page = 1,
    limit = 10,
    search = "",
    user_status = "",
    start_date = "",
    end_date = "",
    content_type = "",
    feed_content_type = "",
  } = params;

  const queryParams = new URLSearchParams();
  queryParams.append("page", page.toString());
  queryParams.append("limit", limit.toString());

  if (search) {
    queryParams.append("search", search);
  }

  if (user_status && user_status !== "all") {
    queryParams.append("user_status", user_status);
  }

  if (start_date) {
    queryParams.append("start_date", start_date);
  }

  if (end_date) {
    queryParams.append("end_date", end_date);
  }

  if (content_type) {
    queryParams.append("content_type", content_type);
  }

  if (feed_content_type) {
    queryParams.append("feed_content_type", feed_content_type);
  }

  const response = await instance.get(
    `/report-reason/list?${queryParams.toString()}`
  );
  
  return response.data;
};

/**
 * Get a single report reason by ID
 * @param {string} reasonId - UUID of the report reason
 * @returns {Promise} API response with report reason details
 */
const getReportReasonById = async (reasonId) => {
  const response = await instance.get(
    `/report-reason/${reasonId}`
  );
  
  return response.data;
};

/**
 * Create a new report reason
 * @param {Object} data - Report reason data
 * @param {string} data.reason - Reason title
 * @param {string} data.description - Reason description
 * @param {number} data.displayOrder - Display order
 * @param {boolean} data.isActive - Active status
 * @returns {Promise} API response with created report reason
 */
const createReportReason = async (data) => {
  const response = await instance.post(
    "/report-reason/create",
    data
  );
  
  return response.data;
};

/**
 * Update an existing report reason
 * @param {string} reasonId - UUID of the report reason
 * @param {Object} data - Updated report reason data
 * @param {string} data.reason - Reason title
 * @param {string} data.description - Reason description
 * @param {number} data.displayOrder - Display order
 * @param {boolean} data.isActive - Active status
 * @returns {Promise} API response with updated report reason
 */
const updateReportReason = async (reasonId, data) => {
  const response = await instance.put(
    `/report-reason/${reasonId}`,
    data
  );
  
  return response.data;
};

/**
 * Dismiss (soft-delete) an invalid report
 * @param {string|number} reportId - ID of the report
 * @param {string} [contentType="user"] - "user" (user_reports, default) or "feed"
 *   (feed_post_reports) - must match which tab the report was read from, or the id is
 *   looked up against the wrong table.
 * @returns {Promise} API response confirming deletion
 */
const deleteReportReason = async (reportId, contentType = "user") => {
  const response = await instance.delete(
    `/report-reason/delete?report_id=${reportId}&content_type=${contentType}`
  );

  return response.data;
};

/**
 * Deactivate the reported user - or, on contentType="feed", the reported post/story's
 * author - and resolve the report
 * @param {string|number} reportId - ID of the report
 * @param {string} adminDeactivateReason - Reason for admin deactivation
 * @param {string} [contentType="user"] - "user" (user_reports, default) or "feed"
 *   (feed_post_reports) - must match which tab the report was read from, or the id is
 *   looked up against the wrong table.
 * @returns {Promise} API response confirming deactivation
 */
const deactivateReportedUser = async (reportId, adminDeactivateReason, contentType = "user") => {
  const response = await instance.put("/report-reason/deactivate", {
    report_id: reportId,
    admin_deactivate_reason: adminDeactivateReason,
    content_type: contentType,
  });

  return response.data;
};

/**
 * Toggle active status of a report reason
 * @param {string} reasonId - UUID of the report reason
 * @param {boolean} isActive - New active status
 * @returns {Promise} API response with updated report reason
 */
const toggleReportReasonStatus = async (reasonId, isActive) => {
  const response = await instance.patch(
    `/report-reason/${reasonId}/toggle-status`,
    { isActive }
  );
  
  return response.data;
};

/**
 * Update display order of report reasons
 * @param {Array} orderData - Array of objects with id and displayOrder
 * @param {string} orderData[].id - UUID of the report reason
 * @param {number} orderData[].displayOrder - New display order
 * @returns {Promise} API response confirming order update
 */
const updateDisplayOrder = async (orderData) => {
  const response = await instance.put(
    "/report-reason/update-order",
    { reasons: orderData }
  );
  
  return response.data;
};

export const reportReasonService = {
  getReportReasons,
  getReportReasonById,
  createReportReason,
  updateReportReason,
  deleteReportReason,
  deactivateReportedUser,
  toggleReportReasonStatus,
  updateDisplayOrder,
};
